// @flow

import _ from 'lodash';

import { ensureArray } from 'app/common/CommonUtils';

import type { ErrorOptions } from 'app/common/types';

// FIXME:
// Flow doesn't really grok old school constructors and prototypes that well,
// so not defining an ErrorType, just using error: any below.
const toJSON = (err: any) => {
  if (typeof err !== 'object') {
    return err;
  }
  return {
    ...err,
    stack: undefined,
  };
};

// A summary is the description of the error. No child error messages or detail.
const errorSummary = function(error: any, options: ErrorOptions) {
  if (typeof options.summary === 'string') {
    return options.summary;
  }
  if (typeof options.summary === 'function') {
    return options.summary(error);
  }
  if (error.summary) {
    return error.summary;
  }
  // No summary here, sorry.
  return '';
};

const hasFeature = (featureFlag, error, options) => {
  if (_.isFunction(options[featureFlag])) {
    return !!options[featureFlag](error);
  }
  return !!options[featureFlag];
};

const getFeature = (feature, error, options) => {
  if (_.isFunction(options[feature])) {
    return options[feature](error);
  }
  return '';
};

// An error has extra detail beyond the summary and child messages.
const errorHasDetail = _.partial(hasFeature, 'hasDetail');

// Extra detail beyond the summary and child messages. Detail is additional information
// that is not shown to the user by default and is not part of the message.
const errorDetail = _.partial(getFeature, 'detail');

// a help link the user can click on for more info
const errorHasHelpLink = _.partial(hasFeature, 'helpLink');
const errorHelpLink = _.partial(getFeature, 'helpLink');

const errorIsPermament = _.partial(hasFeature, 'isPermanent');

// Child error messages of this error.
const errorErrors = function(error: any, options: ErrorOptions) {
  if (typeof options.errors === 'function') {
    return options.errors(error);
  }
  if (_.isArray(error.errors)) {
    return error.errors;
  }
  if (error.responseText) {
    try {
      const response = JSON.parse(error.responseText);
      if (response.errors) {
        return ensureArray(response.errors);
      }
      // Support Django REST Framework exceptions
      // https://github.com/encode/django-rest-framework/blob/903204cd7926e9e6ceaaec1bfbf1ba3ed3330ca4/rest_framework/views.py#L95-L98
      if (_.isString(response.detail)) {
        return [response.detail];
      }
      if (_.isArray(response) && response.length && _.isString(response[0])) {
        return response;
      }
      // If no errors property, return an empty array, *not* undefined, so callers won't blow up on errors.length
      return [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

export const selectErrorMessage = (error: any) => {
  if (!error) {
    return '';
  }
  const message = _.isString(error) ? error : error.message;
  return message || '';
};

export const selectErrorMessages = (errors: any) => {
  return errors
    .map(error => selectErrorMessage(error))
    .filter(message => message);
};

// Try to make a good message for logging.
const errorMessage = function(error, options) {
  const errors = errorErrors(error, options);
  const summary = errorSummary(error, options);
  if (errors.length === 0) {
    return summary;
  }
  return `${summary} (${selectErrorMessages(errors).join(', ')})`;
};

const createErrorConstructor = (name: string, options: any) => {
  function CustomError(detail: any) {
    if (!(this instanceof CustomError)) {
      return new CustomError(detail);
    }

    const attrs = { name };

    // Allow assign to override name.
    if (_.isFunction(options.assign)) {
      _.extend(attrs, options.assign(detail));
    }

    _.extend(this, attrs);
    if (!this.errors) {
      _.extend(this, { errors: errorErrors(this, options) });
    }

    this.message = errorMessage(this, options);

    // Use a try/catch block to capture the stack trace. Capturing the stack trace here is
    // necessary, otherwise we will get the stack trace at the time the new error class was created,
    // rather than when it is instantiated.  We add `message` and `name` so that the stack trace
    // string will match our current error class.
    try {
      throw new Error(this.message);
    } catch (err) {
      err.name = attrs.name;
      this.stack = err.stack;
    }
  }

  CustomError.prototype = (new Error(): any); // make flow typecheck pass, not really worried about it here
  CustomError.prototype.constructor = CustomError;
  CustomError.prototype.inspect = function() {
    return this.message
      ? '[' + name + ': ' + this.message + ']'
      : '[' + name + ']';
  };
  CustomError.prototype.name = name;

  return CustomError;
};

export const createErrorType = function(name: string, options: ErrorOptions) {
  options = options || {};
  const ctor = createErrorConstructor(name, options);
  ctor.prototype = Object.create(Error.prototype);
  ctor.prototype.constructor = ctor;
  ctor.prototype.toJSON = function() {
    return toJSON(this);
  };
  ctor.summary = function(error) {
    return errorSummary(error, options);
  };
  ctor.hasDetail = function(error) {
    return errorHasDetail(error, options);
  };
  ctor.detail = function(error) {
    return errorDetail(error, options);
  };
  ctor.errors = function(error) {
    return errorErrors(error, options);
  };
  ctor.message = function(error) {
    return errorMessage(error, options);
  };
  ctor.hasHelpLink = error => errorHasHelpLink(error, options);
  ctor.helpLink = error => errorHelpLink(error, options);
  ctor.isPermanent = error => errorIsPermament(error, options);
  return ctor;
};

const CommonError = {
  createErrorType,

  createErrorTypes(errorTypes: any) {
    return _.fromPairs(
      Object.keys(errorTypes).map(name => {
        return [name, CommonError.createErrorType(name, errorTypes[name])];
      })
    );
  },
};

export default CommonError;
